/**
 * Professional Academy — API-client van het expertportaal (Netflix-stijl: rijen, tegels,
 * informatie-scherm, opleidersprofiel, activaties).
 * Backend: src/routes/professionalAcademy.ts (/api/professional-academy)
 *
 * De filter-/sorteer-/zoekengine (grid, registers, reistijd) zit in proAcademyApi.ts (/api/pro-academy).
 */
import api from './api';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ProHoofdmap = 'welzijn' | 'trauma' | 'psa' | 'arbo' | 'ld' | 'passie';

export interface ProHoofdmapInfo {
  key: ProHoofdmap;
  rij: number;
  label: string;
  emoji: string;
  kleur: string;
  subtitel: string;
  pe: boolean;
}

export type ProLeervorm = 'e_learning' | 'workshop' | 'opleiding';

export interface ProAccreditatie {
  punten: number;
  register: string | null;
  label: string;
}

export interface ProTraining {
  id: number;
  titel: string;
  soort_opleiding: string | null;
  wanneer: string | null;
  locaties: string | null;
  duur: string | null;
  samenvatting: string | null;
  omslagfoto_url: string | null;
  datum_tijd: string | null;
  locatie_detail: string | null;
  academy_tags: string[];
  subcategorie_emoji: string | null;
  organisatie_naam: string;
  provider_logo: string | null;
  provider_id: number;
  bruto_bedrag: number;
  korting_bedrag: number;
  netto_bedrag: number;
  leervorm: ProLeervorm;
  pitch: string | null;
  populair: boolean;
  certificaat: boolean;
  certificering: string | null;
  rating: { gemiddelde: number; aantal: number } | null;
  beschikbaarheid: {
    status: 'direct' | 'open' | 'plekken' | 'vol';
    heeft_sessies: boolean;
    eerstvolgende_start: string | null;
    vrije_plekken: number | null;
  };
  hoofdmap: ProHoofdmap;
  hoofdmap_label: string;
  hoofdmap_emoji: string;
  hoofdmap_kleur: string;
  pe_punten: number | null;
  pe_register: string | null;
  accreditatie_badge: ProAccreditatie | null;
  inclusief_examen: boolean;
  passie: boolean;
}

export interface ProSessieLes {
  volgnummer: number;
  start_at: string;
  eind_at: string;
  locatie: string | null;
}

export interface ProSessie {
  id: number;
  naam: string | null;
  vrij: number;
  status: 'AVAILABLE' | 'LOCKED_FULL' | 'EXPIRED';
  start_at: string | null;
  lessen: ProSessieLes[];
}

export interface ProTrainingDetail extends ProTraining {
  leerdoelen: string | null;
  voor_wie: string | null;
  doelgroep: string | null;
  programma_onderdelen: string | null;
  niveau: string | null;
  accreditatie: string | null;
  docenten: string | null;
  studiebelasting: string | null;
  toelatingseisen: string | null;
  inclusief_materialen: boolean | null;
  lunch_inbegrepen: boolean | null;
  brochure_url: string | null;
  reviews: { aantal: number; balken: { relevantie: number; toepasbaarheid: number; expertise: number } | null };
  sessies: ProSessie[];
}

export interface ProRij extends ProHoofdmapInfo {
  eigen: boolean;
  trainingen: ProTraining[];
}

export interface ProDiscipline {
  professional_type: string | null;
  label: string | null;
  hoofdmap: ProHoofdmap | null;
}

export interface ProWallet {
  boekjaar: number;
  budget_totaal: number;
  budget_gebruikt: number;
  budget_resterend: number;
  pe_punten_jaar: number;
  korting_pct: number;
}

export interface ProActivatie {
  id: number;
  training_id: number;
  titel: string;
  organisatie_naam: string;
  status: 'aangemeld' | 'bevestigd' | 'betaald' | 'geannuleerd';
  aangemeld_at: string;
  hoofdmap: ProHoofdmap;
  hoofdmap_label: string;
  hoofdmap_emoji: string;
  duur: string | null;
  datum_tijd: string | null;
  wanneer: string | null;
  sessie_id: number | null;
  sessie_naam: string | null;
  sessie_start: string | null;
  aantal_lessen: number;
  heeft_agenda: boolean;
  bruto_bedrag: number;
  korting_bedrag: number;
  netto_bedrag: number;
  pe_punten: number | null;
  pe_register: string | null;
  boekjaar: number | null;
}

export interface ProAcademyOverzicht {
  discipline: ProDiscipline;
  hoofdmappen: ProHoofdmapInfo[];
  rijen: ProRij[];
  wallet: ProWallet;
  activaties: ProActivatie[];
}

export interface ProOpleiderProfiel {
  id: number;
  organisatie_naam: string;
  logo_url: string | null;
  bio: string | null;
  stad: string | null;
  website: string | null;
  header_url: string | null;
  certificeringen: string[];
  score: { gemiddelde: number; aantal: number; cursussen: number } | null;
  aantal_cursussen: number;
  trainingen: ProTraining[];
}

export interface ActiveerResultaat {
  registratie_id: number;
  sessie_id: number | null;
  netto_bedrag: number;
  pe_punten: number | null;
  budget_resterend: number;
}

// ─── Calls ────────────────────────────────────────────────────────────────────

const d = <T>(r: { data: { data: T } }) => r.data.data;

export const getProAcademy = () => api.get<{ data: ProAcademyOverzicht }>('/professional-academy').then(d);

export const getProTrainingen = (filter: { hoofdmap?: string | null; zoek?: string | null }) =>
  api
    .get<{ data: ProTraining[] }>('/professional-academy/trainingen', {
      params: { hoofdmap: filter.hoofdmap || undefined, zoek: filter.zoek || undefined },
    })
    .then(d);

export const getProTraining = (id: number) => api.get<{ data: ProTrainingDetail }>(`/professional-academy/trainingen/${id}`).then(d);

export const getProOpleider = (id: number) => api.get<{ data: ProOpleiderProfiel }>(`/professional-academy/opleiders/${id}`).then(d);

export const activeerProTraining = (id: number, sessieId: number | null) =>
  api.post<{ data: ActiveerResultaat }>(`/professional-academy/trainingen/${id}/activeer`, { sessie_id: sessieId }).then(d);

export const downloadProAgenda = async (registratieId: number, titel: string) => {
  const r = await api.get(`/professional-academy/activaties/${registratieId}/agenda.ics`, { responseType: 'blob' });
  const url = URL.createObjectURL(r.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `belagom-academy-${titel.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 50) || 'agenda'}.ics`;
  a.click();
  URL.revokeObjectURL(url);
};
